<script setup lang="ts">
import { ElCheckbox, ElTooltip, type CheckboxProps } from "element-plus";
import CheckboxSettingSkeleton from "@/components/skeletons/CheckboxSettingSkeleton.vue";
import { ref, watch } from "vue";

export type Props = {
  value?: boolean;
  loading?: boolean;
  disabledMessage?: string;
  label: string;
} & Partial<CheckboxProps>;

const props = defineProps<Omit<Props, "modelValue">>();
const valueCopy = ref(props.value);
watch(
  () => props.value,
  (val) => {
    valueCopy.value = val;
  }
);
</script>

<template>
  <CheckboxSettingSkeleton :loading="loading">
    <div class="setting-checkbox-wrapper">
      <el-checkbox
        v-model="valueCopy"
        v-cy="label"
        class="setting-checkbox"
        v-bind="{ ...props, ...$attrs }"
      >
        {{ label }}
        <span v-if="disabled"><slot name="disabled-suffix" /> </span>
        <span class="checkbox-suffix"><slot name="suffix" /></span>
        <el-tooltip
          v-if="disabled && disabledMessage"
          :content="disabledMessage"
          :show-after="500"
          placement="top"
        >
          <font-awesome-icon
            class="help-icon"
            :icon="['fal', 'circle-question']"
          />
        </el-tooltip>
      </el-checkbox>
      <p class="description info">
        <span><slot /></span>
      </p>
    </div>
  </CheckboxSettingSkeleton>
</template>

<style lang="css" scoped>
.setting-checkbox-wrapper {
  margin: 17px 0;
}

.setting-checkbox {
  --so-checkbox-text-color: var(--so-text-color-primary);
  --so-checkbox-checked-text-color: var(--so-text-color-primary);
  height: 20px;
  font-weight: 400;
}

.checkbox-suffix {
  margin-left: 10px;
}

.setting-checkbox :deep(.so-checkbox__label) {
  display: inline-flex;
  align-items: center;
  white-space: pre-wrap;
}

.help-icon {
  margin-left: 8px;
  font-size: 1.125rem;
  color: var(--so-text-color-regular);
}

.description {
  margin-top: -3px;
  margin-left: 24px;
}
</style>
